import type { JSX } from 'solid-js'
import { A } from '@solidjs/router'
import {
  Calendar,
  LayoutDashboard,
  Settings,
  Menu,
  X,
  Banknote,
  Contact,
  BarChart3,
  HelpCircle,
  Package,
  Wallet,
} from 'lucide-solid'
import { createSignal, Show } from 'solid-js'

export type TenantRole = 'owner' | 'manager' | 'staff'

// minRole per nav entry. Hierarchy: owner > manager > staff.
const NAV = (slug: string): Array<{ href: string; label: string; icon: typeof LayoutDashboard; minRole: TenantRole }> => [
  { href: `/t/${slug}/dashboard`, label: 'Dashboard', icon: LayoutDashboard, minRole: 'staff' },
  { href: `/t/${slug}/agenda`, label: 'Agenda', icon: Calendar, minRole: 'staff' },
  { href: `/t/${slug}/clienti`, label: 'Clienti', icon: Contact, minRole: 'manager' },
  { href: `/t/${slug}/cassa`, label: 'Cassa', icon: Banknote, minRole: 'manager' },
  { href: `/t/${slug}/magazzino`, label: 'Magazzino', icon: Package, minRole: 'staff' },
  { href: `/t/${slug}/gestione`, label: 'Gestione', icon: Wallet, minRole: 'manager' },
  { href: `/t/${slug}/statistiche`, label: 'Statistiche', icon: BarChart3, minRole: 'manager' },
  { href: `/t/${slug}/settings`, label: 'Impostazioni', icon: Settings, minRole: 'manager' },
  { href: `/t/${slug}/assistenza`, label: 'Assistenza', icon: HelpCircle, minRole: 'staff' },
]

const ROLE_RANK: Record<TenantRole, number> = { owner: 3, manager: 2, staff: 1 }

export function roleSatisfies(actual: TenantRole | null | undefined, required: TenantRole): boolean {
  if (!actual) return false
  return ROLE_RANK[actual] >= ROLE_RANK[required]
}

export const AppShellTenant = (props: {
  slug: string
  tenantName: string
  logoUrl?: string | null
  /** booking_theme name from account profile — applies palette globally */
  theme?: string | null
  userName?: string
  title?: string
  actions?: JSX.Element
  children: JSX.Element
  role?: TenantRole | null
}): JSX.Element => {
  const [drawerOpen, setDrawerOpen] = createSignal(false)
  // Default to 'owner' when role is unknown — keeps legacy callers' UX
  // unchanged until they pass role explicitly.
  const visibleNav = () =>
    NAV(props.slug).filter((n) => roleSatisfies(props.role ?? 'owner', n.minRole))

  return (
    <div
      class={
        'grid grid-cols-[240px_1fr] max-xl:grid-cols-1 bg-bg salun-booking-theme-' +
        (props.theme ?? 'default').replace(/[^a-z0-9-]/gi, '')
      }
    >
      {/* Backdrop for mobile */}
      <Show when={drawerOpen()}>
        <div
          class="fixed inset-0 z-30 bg-fg/20 xl:hidden"
          onClick={() => setDrawerOpen(false)}
        />
      </Show>

      <aside
        class="bg-surface border-r border-border flex flex-col xl:sticky xl:top-0 xl:h-screen max-xl:fixed max-xl:inset-y-0 max-xl:left-0 max-xl:w-64 max-xl:z-40 max-xl:transition-transform max-xl:-translate-x-full"
        classList={{ 'max-xl:translate-x-0': drawerOpen() }}
      >
        <div class="px-5 py-5 border-b border-border">
          <Show
            when={props.logoUrl}
            fallback={<div class="w-12 h-12 bg-accent rounded-md mb-3" aria-hidden />}
          >
            <img
              src={props.logoUrl!}
              alt={props.tenantName}
              class="w-12 h-12 rounded-md object-cover mb-3"
            />
          </Show>
          <div class="font-display font-semibold text-fg truncate">{props.tenantName}</div>
          <div class="text-xs text-muted">/{props.slug}</div>
        </div>
        <nav class="flex-1 py-3 overflow-y-auto">
          {visibleNav().map((n) => (
            <A
              href={n.href}
              class="flex items-center gap-3 px-5 py-2 text-sm text-muted hover:text-fg hover:bg-surface-alt transition-colors"
              activeClass="text-accent-strong bg-accent-soft font-medium"
            >
              <n.icon size={18} />
              <span>{n.label}</span>
            </A>
          ))}
        </nav>
        <Show when={props.userName}>
          <div class="px-5 py-4 border-t border-border text-sm text-muted">{props.userName}</div>
        </Show>
      </aside>

      <div class="flex flex-col min-w-0 min-h-screen">
        <header class="sticky top-0 z-20 h-14 bg-surface border-b border-border px-4 xl:px-6 flex items-center gap-4 xl:hidden">
          <button
            class="xl:hidden text-muted hover:text-fg"
            aria-label="Menu"
            onClick={() => setDrawerOpen((v) => !v)}
          >
            <Show when={drawerOpen()} fallback={<Menu size={20} />}>
              <X size={20} />
            </Show>
          </button>
          <h1 class="font-display text-lg font-semibold text-fg flex-1 truncate">{props.title}</h1>
          {props.actions}
        </header>
        <main class="flex-1 p-4 xl:p-6 salun-page-enter">{props.children}</main>
      </div>
    </div>
  )
}
